import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Lock, Mail, LogOut, Download, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "new", label: "Nieuw", tone: "bg-emerald-100 text-emerald-800" },
  { v: "contacted", label: "Gebeld", tone: "bg-sky-100 text-sky-800" },
  { v: "qualified", label: "Gekwalificeerd", tone: "bg-amber-100 text-amber-800" },
  { v: "won", label: "Gewonnen", tone: "bg-teal-100 text-teal-800" },
  { v: "lost", label: "Verloren", tone: "bg-rose-100 text-rose-800" },
];

export function PartnerLoginPage() {
  const { login, user } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Already logged in as partner → redirect
    if (user && (user.role === "partner" || user.role === "admin")) {
      nav("/partner-portal");
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      const role = data?.user?.role;
      if (role !== "partner" && role !== "admin") {
        setErr("Dit account heeft geen partner-toegang.");
        return;
      }
      toast.success("Welkom terug");
      nav("/partner-portal");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Inloggen mislukt");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2" data-testid="partner-login-page">
      <div className="flex items-center justify-center p-8 sm:p-12 bg-[#FDFBF7]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742]">Partner portaal</div>
            <h1 className="display text-3xl font-semibold text-[#1F3A2E] mt-2">Jouw leads. Jouw overzicht.</h1>
            <p className="text-sm text-[#5B6B61] mt-2">Log in om alleen de aanvragen uit jouw kanaal te zien en te beheren.</p>
          </div>
          {err && <div className="text-sm rounded-md bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2" data-testid="partner-login-error">{err}</div>}
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#1F3A2E]/70">E-mail</span>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F3A2E]/50" />
                <input data-testid="partner-login-email" className="w-full rounded-md border border-[#1F3A2E]/20 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#1F3A2E]" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#1F3A2E]/70">Wachtwoord</span>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F3A2E]/50" />
                <input data-testid="partner-login-password" className="w-full rounded-md border border-[#1F3A2E]/20 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#1F3A2E]" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required />
              </div>
            </label>
          </div>
          <button data-testid="partner-login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 bg-[#1F3A2E] text-[#FDFBF7] hover:bg-[#2d4a3e] transition disabled:opacity-60">
            {loading ? "Bezig..." : "Inloggen"} <ChevronRight className="w-4 h-4" />
          </button>
          <div className="text-xs text-[#5B6B61]">Geen partner-account?{" "}
            <Link to="/" className="underline">terug naar de site</Link>.
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative overflow-hidden bg-[#1F3A2E] text-[#FDFBF7]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3A2E] via-[#28503e] to-[#0f2418]" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-end">
          <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#D97742]">White-label</div>
          <div className="display text-3xl md:text-4xl font-semibold mt-2 max-w-md">Eén overzicht. Alle leads uit jouw kanaal. Geen admin-rechten nodig.</div>
          <ul className="mt-6 text-sm space-y-2 text-[#FDFBF7]/80 max-w-md">
            <li>· Live lead-updates op status, notities en contactgegevens</li>
            <li>· CSV export voor je maandelijkse rapportage</li>
            <li>· Direct PDF-offerte downloaden per lead</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LeadRow({ lead, onSelect, selected }) {
  const s = STATUSES.find((x) => x.v === (lead.status || "new")) || STATUSES[0];
  return (
    <button
      onClick={onSelect}
      data-testid={`partner-lead-row-${lead.id}`}
      className={`w-full text-left px-4 py-3 border-b border-[#1F3A2E]/10 hover:bg-[#F4F1EA] transition ${selected ? "bg-[#F4F1EA]" : "bg-white"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#1F3A2E] truncate">{lead.company_name || lead.contact_name || "—"}</div>
          <div className="text-xs text-[#5B6B61] truncate">{lead.contact_email} · {lead.quote_number}</div>
        </div>
        <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${s.tone}`}>{s.label}</span>
      </div>
      <div className="mt-1 text-xs text-[#5B6B61]">€{Number(lead.total_once || 0).toLocaleString("nl-NL")} eenmalig · €{Number(lead.subtotal_monthly || 0).toLocaleString("nl-NL")}/mnd</div>
    </button>
  );
}

function LeadDetail({ lead, onUpdate }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [notes, setNotes] = useState(lead.sales_notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus(lead.status || "new"); setNotes(lead.sales_notes || ""); }, [lead.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/partner/leads/${lead.id}`, { status, sales_notes: notes });
      toast.success("Lead bijgewerkt");
      onUpdate(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon niet opslaan");
    } finally { setSaving(false); }
  };

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${lead.id}/pdf`;
  return (
    <div className="p-6" data-testid="partner-lead-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1F3A2E]">{lead.company_name || lead.contact_name}</h3>
          <div className="text-xs text-[#5B6B61] mt-0.5">Offerte {lead.quote_number} · {(lead.created_at || "").slice(0,10)}</div>
        </div>
        <a data-testid="partner-lead-pdf" href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1.5 rounded-full border border-[#1F3A2E]/20 px-3 py-1.5 hover:bg-[#F4F1EA]">
          <ExternalLink className="w-3.5 h-3.5" /> PDF
        </a>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 mt-5 text-sm">
        <div><dt className="text-xs text-[#5B6B61]">Contact</dt><dd className="text-[#1F3A2E]">{lead.contact_name}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">E-mail</dt><dd className="text-[#1F3A2E]"><a className="underline" href={`mailto:${lead.contact_email}`}>{lead.contact_email}</a></dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Telefoon</dt><dd className="text-[#1F3A2E]">{lead.contact_phone || "—"}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Adres</dt><dd className="text-[#1F3A2E]">{[lead.address_street, lead.address_postal, lead.address_city, lead.country].filter(Boolean).join(", ") || "—"}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Sector</dt><dd className="text-[#1F3A2E]">{lead.sector_slug || "—"}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Installatie</dt><dd className="text-[#1F3A2E]">{lead.install_mode === "install" ? "Laten installeren" : "Zelf installeren"}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Eenmalig</dt><dd className="text-[#1F3A2E]">€{Number(lead.total_once || 0).toLocaleString("nl-NL")}</dd></div>
        <div><dt className="text-xs text-[#5B6B61]">Maandelijks</dt><dd className="text-[#1F3A2E]">€{Number(lead.subtotal_monthly || 0).toLocaleString("nl-NL")}</dd></div>
      </dl>

      <div className="mt-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#1F3A2E]/70 mb-2">Status</div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              data-testid={`partner-status-${s.v}`}
              onClick={() => setStatus(s.v)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${status === s.v ? "bg-[#1F3A2E] border-[#1F3A2E] text-[#FDFBF7]" : "bg-white border-[#1F3A2E]/20 text-[#1F3A2E] hover:bg-[#F4F1EA]"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <label className="text-xs font-semibold uppercase tracking-wider text-[#1F3A2E]/70">Interne notities</label>
        <textarea
          data-testid="partner-lead-notes"
          className="w-full mt-2 rounded-md border border-[#1F3A2E]/20 bg-white p-3 text-sm min-h-[120px] focus:outline-none focus:border-[#1F3A2E]"
          placeholder="Gesprek, afspraken, volgende stap..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <button data-testid="partner-lead-save" disabled={saving} onClick={save} className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#1F3A2E] text-[#FDFBF7] hover:bg-[#2d4a3e] transition disabled:opacity-60 text-sm">
        {saving ? "Opslaan..." : "Opslaan"}
      </button>
    </div>
  );
}

export default function PartnerPortalPage() {
  const { user, logout } = useApp();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { nav("/partner-login"); return; }
    if (user.role !== "partner" && user.role !== "admin") { nav("/partner-login"); return; }
    (async () => {
      setLoading(true);
      try {
        const [{ data: me1 }, { data: leads1 }] = await Promise.all([
          api.get("/partner/me"),
          api.get("/partner/leads"),
        ]);
        setMe(me1);
        setLeads(leads1);
        if (leads1.length) setSelected(leads1[0]);
      } catch (e) {
        toast.error("Kon portal niet laden");
      } finally { setLoading(false); }
    })();
  }, [user]);

  const filtered = useMemo(() => {
    return (leads || []).filter((l) => {
      if (statusFilter && (l.status || "new") !== statusFilter) return false;
      if (filter) {
        const s = filter.toLowerCase();
        const hay = [l.company_name, l.contact_name, l.contact_email, l.quote_number].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [leads, filter, statusFilter]);

  const exportCsv = () => {
    const now = new Date();
    const y = now.getFullYear(); const m = String(now.getMonth()+1).padStart(2,"0");
    const since = `${y}-${m}-01`;
    const until = now.toISOString().slice(0,10);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/partner/leads/export?since=${since}&until=${until}`;
    window.open(url, "_blank");
  };

  const openMonthlyReport = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/partner/monthly-report`;
    window.open(url, "_blank");
  };

  const doLogout = async () => {
    await logout(); nav("/partner-login");
  };

  const partnerName = me?.partner?.name || me?.user?.company || "Partner";
  const accent = me?.partner?.primary_color || "#1F3A2E";

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#F4F1EA]" data-testid="partner-portal-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {me?.partner?.logo_url && (
              <img src={me.partner.logo_url} alt={partnerName} className="h-10 object-contain" />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Partner portaal</div>
              <h1 className="text-2xl font-semibold text-[#1F3A2E]">{partnerName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="partner-portal-monthly-report" onClick={openMonthlyReport} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[#FDFBF7] text-sm shadow-sm" style={{ backgroundColor: accent }}>
              <ExternalLink className="w-4 h-4" /> Maandoverzicht
            </button>
            <button data-testid="partner-portal-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-[#1F3A2E]/20 text-sm hover:bg-[#FDFBF7]">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button data-testid="partner-portal-logout" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[#FDFBF7] text-sm" style={{ backgroundColor: "#0F2A1E" }} aria-label="Uitloggen">
              <LogOut className="w-4 h-4" /> Uitloggen
            </button>
          </div>
        </header>

        <div className="grid lg:grid-cols-[360px_1fr] gap-4">
          <aside className="bg-white rounded-2xl border border-[#1F3A2E]/10 overflow-hidden">
            <div className="p-4 border-b border-[#1F3A2E]/10 space-y-2">
              <input
                data-testid="partner-portal-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Zoek op bedrijf, contact, e-mail..."
                className="w-full rounded-md border border-[#1F3A2E]/20 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1F3A2E]"
              />
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setStatusFilter("")} className={`text-[11px] px-2 py-1 rounded-full border ${!statusFilter ? "bg-[#1F3A2E] text-[#FDFBF7] border-[#1F3A2E]" : "bg-white border-[#1F3A2E]/20 text-[#1F3A2E]"}`}>Alle</button>
                {STATUSES.map(s => (
                  <button key={s.v} onClick={() => setStatusFilter(s.v === statusFilter ? "" : s.v)} className={`text-[11px] px-2 py-1 rounded-full border ${statusFilter === s.v ? "bg-[#1F3A2E] text-[#FDFBF7] border-[#1F3A2E]" : "bg-white border-[#1F3A2E]/20 text-[#1F3A2E]"}`}>{s.label}</button>
                ))}
              </div>
            </div>
            <div data-testid="partner-lead-list" className="max-h-[70vh] overflow-y-auto">
              {loading && <div className="p-6 text-sm text-[#5B6B61]">Leads laden...</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-sm text-[#5B6B61]" data-testid="partner-portal-empty">Geen leads gevonden voor de huidige filter.</div>
              )}
              {filtered.map((l) => (
                <LeadRow key={l.id} lead={l} selected={selected?.id === l.id} onSelect={() => setSelected(l)} />
              ))}
            </div>
          </aside>
          <section className="bg-white rounded-2xl border border-[#1F3A2E]/10 min-h-[60vh]">
            {selected ? (
              <LeadDetail lead={selected} onUpdate={(u) => {
                setSelected(u);
                setLeads((xs) => xs.map((x) => x.id === u.id ? u : x));
              }} />
            ) : (
              <div className="p-10 text-sm text-[#5B6B61]">Selecteer een lead om details te zien.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
